import camelcaseKeys from "camelcase-keys";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useMutationState, useQuery, useQueryClient } from "@tanstack/react-query";

import config from "../../config";
import { AXIOS } from "../axios";
import { useError } from "../../components/status/ErrorProvider";
import { useSuccess } from "../../components/status/SuccessProvider";
import { IDP, STATUS, useAuth, User } from "../../components/auth/AuthProvider";
import { MetaSchema, PaginationSchema } from "../pagination";

const baseUrl = config.url.CONFG;

export type TermsConfig = {
  text: string;
  hash: string;
  version: string;
  current: boolean;
  updatedAt: Date;
};

export type DlmConfig = {
  token: string;
  groupId: string;
  pushUrl: string;
  verifyUrl: string;
};

export type Organization = {
  id: string;
  idp: IdpConfig[];
  url: { public: string; private: string };
  name: string;
  tags: string[];
  domain: string;
  online: boolean;
  acronym: string;
  lastActive: Date;
  partNumber: { unique: boolean; required: boolean };
  description: string;
  activeUsers: number;
  dlm: DlmConfig;
  terms: TermsConfig[];
};

type OrganizationConnect = { id: string };

export type IdpConfig = {
  name: IDP;
  tenantId: string;
  clientId: string;
  clientSecret: string;
  authority: string;
};

type OrganizationCreate = {
  name: string;
  domain: string;
  acronym: string;
  urlPublic: string;
  urlPrivate: string;
  description: string;
  idp: IdpConfig[] | null;
  dlm: { token: string; pushUrl: string; verifyUrl: string };
};

type OrganizationRemove = {
  id: string;
};

type EditProfile = {
  lastName: string;
  firstName: string;
  newPassword: string;
  newPasswordConfirmation: string;
  currentPassword: string;
};

type OrganizationAddIdp = IdpConfig[];

type OrganizationEditIdp = IdpConfig;

type OrganizationAddUsers = {
  role: User["activeOrganization"]["role"] | undefined;
  organizationId: string;
  userIds: readonly string[];
  comment?: string;
};

type OrganizationRemoveUsers = {
  organizationId: string;
  userIds: readonly string[];
  comment?: string;
};

type OrganizationEditDescription = {
  id: string;
  description: string;
};

type OrganizationEditTerms = {
  text: string;
};

type OrganizationEditSettings = {
  name: string;
  domain: string;
  acronym: string;
  urlPublic: string;
  urlPrivate: string;
  partNumber: Organization["partNumber"];
  description: string;
};

type EditIdp = { id: string; idp: User["idp"] };
type EditRole = { id: string; role: User["activeOrganization"]["role"] };
type EditStatus = { id: string; status: STATUS };

type SysEditRole = {
  id: string;
  role: User["activeOrganization"]["role"];
  organizationId: string;
};

type SysEditStatus = {
  id: string;
  status: STATUS;
  organizationId: string;
};

type SysOrganizationEditSettings = {
  id: string;
  domain: string;
  acronym: string;
  urlPublic: string;
  urlPrivate: string;
  description: string;
  dlm: DlmConfig;
};

type DownloadTermsSchema = { hash?: string };

export const useConfgTerms = () => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, DownloadTermsSchema>(
    {
      mutationKey: [`CONFG DOWNLOAD TERMS`],
      mutationFn: (props: DownloadTermsSchema) =>
        AXIOS.post(`${baseUrl}/download/terms`, props, {
          responseType: "json"
        }),
      onError: (error) => {
        console.log(error);

        const data = error?.response?.data;
        const message = data?.error ?? data?.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: ({ data, headers }) => {
        const blob = new Blob([data.data], { type: "text/plain;charset=utf-8" });

        const filename = headers["content-filename"] || `${new Date().toISOString()}.text`;

        // create & download file
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        // clean up
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        setSuccess(`Downloaded: ${filename}`);
      }
    }
  );

  return mutation;
};

export const useConfgUsers = (body?: PaginationSchema) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    { data: User[]; meta: MetaSchema }
  >({
    queryKey: [`CONFG USERS ${JSON.stringify(body)}`],
    queryFn: () => AXIOS.post(`${baseUrl}/users`, body),
    select: (data) => camelcaseKeys(data.data, { deep: true })
  });
};

export const useConfgEditProfile = () => {
  const { setUser } = useAuth();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, EditProfile>({
    mutationKey: [`CONFG EDIT PROFILE`],
    mutationFn: (props: EditProfile) => AXIOS.post(`${baseUrl}/users/edit/profile`, props),
    onSuccess: (data) => {
      const user = camelcaseKeys(data.data.data, { deep: true });

      // add user to local state
      setUser(user);

      setSuccess("Profile updated");
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgOrganizations = (body?: PaginationSchema) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    { data: Organization[]; meta: MetaSchema }
  >({
    queryKey: [`CONFG ORGANIZATIONS ${JSON.stringify(body)}`],
    queryFn: () => AXIOS.post(`${baseUrl}/organizations`, body),
    select: (data) => camelcaseKeys(data.data, { deep: true }),
    staleTime: 1000 * 60 * 2
  });
};

export const useConfgOrganizationSettings = () => {
  return useQuery<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { data: Organization }>({
    queryKey: [`CONFG ORGANIZATION SETTINGS`],
    queryFn: () => AXIOS.post(`${baseUrl}/organization/settings`),
    select: (data) => camelcaseKeys(data.data, { deep: true })
  });
};

export const useConfgOrganizationDlmVerify = () => {
  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>>({
    mutationKey: [`CONFG ORGANIZATION DLM VERIFY`],
    mutationFn: () => AXIOS.post(`${baseUrl}/organization/dlm/verify`)
  });
  return mutation;
};

export const useConfgOrganizationDlmVerifyGlobalStatus = () => {
  const dlmVerify = useMutationState({
    filters: { mutationKey: ["CONFG ORGANIZATION DLM VERIFY"] },
    select: (mutation) => mutation.state.status // select the data you need
  });

  const isError = dlmVerify.includes("error");
  const isSuccess = dlmVerify.includes("success");

  return { isError, isSuccess, dlmVerify };
};

export const useConfgOrganizationCreate = (body?: PaginationSchema) => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, OrganizationCreate>({
    mutationKey: [`CONFG ORGANIZATIONS CREATE ${JSON.stringify(body)}`],
    mutationFn: (props: OrganizationCreate) => AXIOS.post(`${baseUrl}/organization/create`, props),
    onSuccess: (data) => {
      setSuccess("Organization created");
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgOrganizationConnect = () => {
  const auth = useAuth();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, OrganizationConnect>(
    {
      mutationKey: [`CONFG ORGANIZATIONS CONNECT`],
      mutationFn: (props: OrganizationConnect) => AXIOS.post(`${baseUrl}/organization/connect`, props),
      onSuccess: (data) => {
        const user = camelcaseKeys(data.data.data, { deep: true }) as User;

        // add to user state
        auth.setUser(user);

        // add to global axios header
        AXIOS.defaults.headers.common["x-organization-id"] = user.activeOrganization.id;

        // add to local storage
        localStorage.setItem("SEMIAUTH_ORG_ID", user.activeOrganization.id);
        localStorage.setItem("SEMIAUTH_ORG_NAME", user.activeOrganization.name);

        setSuccess("Connected");
      },
      onError: (error) => {
        const data = error?.response?.data;
        const message = data?.error ?? data?.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      }
    }
  );

  return mutation;
};

export const useConfgOrganizationEditSettings = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    OrganizationEditSettings
  >({
    mutationKey: [`CONFG ORGANIZATION EDIT SETTINGS`],
    mutationFn: (props: OrganizationEditSettings) => AXIOS.post(`${baseUrl}/organization/edit/settings`, props),
    onSuccess: (data) => {
      setSuccess("Settings updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG ORGANIZATION")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgOrganizationEditTerms = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    OrganizationEditTerms
  >({
    mutationKey: [`CONFG ORGANIZATION EDIT TERMS`],
    mutationFn: (props: OrganizationEditTerms) => AXIOS.post(`${baseUrl}/organization/edit/terms`, props),
    onSuccess: (data) => {
      setSuccess("Terms updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG ORGANIZATION")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgOrganizationDownloadTerms = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, DownloadTermsSchema>(
    {
      mutationKey: [`CONFG ORGANIZATION DOWNLOAD TERMS`],
      mutationFn: (props: DownloadTermsSchema) =>
        AXIOS.post(`${baseUrl}/organization/download/terms`, props, {
          responseType: "json"
        }),
      onError: (error) => {
        console.log(error);

        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: ({ data, headers }) => {
        const blob = new Blob([data.data], { type: "text/plain;charset=utf-8" });

        const filename = headers["content-filename"] || `${new Date().toISOString()}.text`;

        // create & download file
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        // clean up
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        setSuccess(`Downloaded: ${filename}`);

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("CONFG ORGANIZATION")
        });
      }
    }
  );

  return mutation;
};

export const useConfgOrganizationEditIdp = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, OrganizationEditIdp>(
    {
      mutationKey: [`CONFG ORGANIZATION EDIT IDP`],
      mutationFn: (props: OrganizationEditIdp) => AXIOS.post(`${baseUrl}/organization/edit/idp`, props),
      onSuccess: (data) => {
        setSuccess("Provider updated");

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("CONFG ORGANIZATION")
        });
      },
      onError: (error) => {
        const data = error?.response?.data;
        const message = data?.error ?? data?.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      }
    }
  );

  return mutation;
};

export const useConfgOrganizationAddIdp = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, OrganizationAddIdp>({
    mutationKey: [`CONFG ORGANIZATION ADD IDP`],
    mutationFn: (idp: OrganizationAddIdp) => AXIOS.post(`${baseUrl}/organization/add/idp`, { idp }),
    onSuccess: (data) => {
      setSuccess("Provider added");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG ORGANIZATION")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgOrganizationRemoveIdp = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, IDP>({
    mutationKey: [`CONFG ORGANIZATION REMOVE IDP`],
    mutationFn: (name: IDP) => AXIOS.post(`${baseUrl}/organization/remove/idp`, { name }),
    onSuccess: (data) => {
      setSuccess("Provider removed");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG ORGANIZATION")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgOrganizationUserEditIdp = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, EditIdp>({
    mutationKey: [`CONFG EDIT IDP`],
    mutationFn: (props: EditIdp) => AXIOS.post(`${baseUrl}/organization/users/edit/idp`, props),
    gcTime: Infinity,
    onError: (error) => {
      const message = error.response?.data.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: () => {
      setSuccess("Identify provider updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG ORGANIZATIONS")
      });
    }
  });

  return mutation;
};

export const useConfgOrganizationUserEditRole = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, EditRole>({
    mutationKey: [`CONFG EDIT ROLE`],
    mutationFn: (props: EditRole) => AXIOS.post(`${baseUrl}/organization/users/edit/role`, props),
    gcTime: Infinity,
    onError: (error) => {
      const message = error.response?.data.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: () => {
      setSuccess("Role updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });
    }
  });

  return mutation;
};

export const useConfgOrganizationUserEditStatus = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, EditStatus>({
    mutationKey: [`CONFG EDIT STATUS`],
    mutationFn: (props: EditStatus) => AXIOS.post(`${baseUrl}/organization/users/edit/status`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: () => {
      setSuccess("Account updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });
    }
  });

  return mutation;
};

export const useConfgOrganizationUserRemove = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, string>({
    mutationKey: [`CONFG REMOVE USER`],
    mutationFn: (id: string) => AXIOS.post(`${baseUrl}/organization/users/remove`, { id }),
    onSuccess: (data) => {
      setSuccess("Account removed");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

// note: the following hooks are reserved for `sys.admin` only

export const useConfgSysOrganizations = (body?: PaginationSchema) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    { data: Organization[]; meta: MetaSchema }
  >({
    queryKey: [`CONFG SYS ORGANIZATIONS ${JSON.stringify(body)}`],
    queryFn: () => AXIOS.post(`${baseUrl}/sys/organizations`, body),
    select: (data) => camelcaseKeys(data.data, { deep: true }),
    staleTime: 1000 * 60 * 2
  });
};

export const useConfgSysOrganizationCreate = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, OrganizationCreate>({
    mutationKey: [`CONFG SYS ORGANIZATION CREATE`],
    mutationFn: (props: OrganizationCreate) => AXIOS.post(`${baseUrl}/sys/organization/create`, props),
    onSuccess: (data) => {
      setSuccess("Organization created");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG SYS ORGANIZATIONS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgSysOrganizationRemove = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, OrganizationRemove>({
    mutationKey: [`CONFG SYS ORGANIZATION REMOVE`],
    mutationFn: (props: OrganizationRemove) => AXIOS.post(`${baseUrl}/sys/organization/remove`, props),
    onSuccess: (data) => {
      setSuccess("Organization removed");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG SYS ORGANIZATIONS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgSysOrganizationDlmVerify = () => {
  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { id: string }>({
    mutationKey: [`CONFG SYS ORGANIZATION DLM VERIFY`],
    mutationFn: (props: { id: string }) => AXIOS.post(`${baseUrl}/sys/organization/dlm/verify`, props)
  });

  return mutation;
};

export const useConfgSysOrganizationEditSettings = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    SysOrganizationEditSettings
  >({
    mutationKey: [`CONFG SYS ORGANIZATIONS EDIT SETTINGS`],
    mutationFn: (props: SysOrganizationEditSettings) => AXIOS.post(`${baseUrl}/sys/organization/edit/settings`, props),
    onSuccess: (data) => {
      setSuccess("Settings updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG SYS ORGANIZATIONS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgSysOrganizationEditDescription = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    OrganizationEditDescription
  >({
    mutationKey: [`CONFG SYS ORGANIZATIONS EDIT DESCRIPTION`],
    mutationFn: (props: OrganizationEditDescription) => AXIOS.post(`${baseUrl}/sys/organization/edit/description`, props),
    onSuccess: (data) => {
      setSuccess("Description updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG SYS ORGANIZATIONS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgSysOrganizationUsersEditIdp = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, EditIdp>({
    mutationKey: [`CONFG SYS ORGANIZATIONS USERS EDIT IDP`],
    mutationFn: (props: EditIdp) => AXIOS.post(`${baseUrl}/sys/organization/users/edit/idp`, props),
    onSuccess: (data) => {
      setSuccess("IDP updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgSysOrganizationUsersEditRole = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, SysEditRole>({
    mutationKey: [`CONFG SYS ORGANIZATIONS USERS EDIT ROLE`],
    mutationFn: (props: SysEditRole) => AXIOS.post(`${baseUrl}/sys/organization/users/edit/role`, props),
    onSuccess: (data) => {
      setSuccess("Role updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgSysOrganizationUsersEditStatus = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, SysEditStatus>({
    mutationKey: [`CONFG SYS ORGANIZATIONS USERS EDIT STATUS`],
    mutationFn: (props: SysEditStatus) => AXIOS.post(`${baseUrl}/sys/organization/users/edit/status`, props),
    onSuccess: (data) => {
      setSuccess("Account updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgSysOrganizationUsersAdd = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    OrganizationAddUsers
  >({
    mutationKey: [`CONFG SYS ORGANIZATIONS USERS ADD`],
    mutationFn: (props: OrganizationAddUsers) => AXIOS.post(`${baseUrl}/sys/organization/users/add`, props),
    onSuccess: (data) => {
      setSuccess("Organization updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};

export const useConfgSysOrganizationUsersRemove = () => {
  const queryClient = useQueryClient();

  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    OrganizationRemoveUsers
  >({
    mutationKey: [`CONFG SYS ORGANIZATIONS USERS REMOVE`],
    mutationFn: (props: OrganizationRemoveUsers) => AXIOS.post(`${baseUrl}/sys/organization/users/remove`, props),
    onSuccess: (data) => {
      setSuccess("Organization updated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG USERS")
      });

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("CONFG ORGANIZATIONS")
      });
    },
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    }
  });

  return mutation;
};
